import { useEffect, useState, useRef, useMemo, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import ProductItem from "./ProductItem";
import { PlusOutlined, EditOutlined, ExclamationCircleOutlined, InfoCircleOutlined, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, message, notification, Popover } from "antd";
import Add from "../products/Add";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../utils/apiClient";
import checkPosType from "../../utils/posType";
import { useDispatch, useSelector } from "react-redux";
import { addProduct as addProductToCart } from "../../redux/cartSlice";
import {
  HS_CODE_HINTS,
  UOM_NUMBERS_PIECES_UNITS,
  UOM_OPTIONS,
  SALES_TAX_RATES,
  DEFAULT_CATEGORY,
  INVOICE_TYPE_POS,
  LOW_STOCK_THRESHOLD,
} from "../../utils/constants";

const LOW_STOCK_NOTIFICATION_KEY = "low-stock";

const Products = ({ categories, selectedCategory = "All", searched }) => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const limit = 100;

  const navigate = useNavigate();
  const containerRef = useRef(null); // container ref for scrolling
  const dispatch = useDispatch();
  const [quickAddForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const user = useSelector((state) => state.user);

  const { posType } = useMemo(() => {
    const businessName = user?.businessName || "";
    return { posType: checkPosType(businessName) };
  }, [user]);

  const getProducts = async (overrideSkip = null, replace = false) => {
    if (overrideSkip === null && !hasMore) return;

    const effectiveSkip = overrideSkip ?? skip;
    const currentPage = Math.floor(effectiveSkip / limit) + 1;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: String(limit),
        page: String(currentPage),
      });
      if (searched) params.append("search", searched);
      if (user?.role !== "cashier" && user?.selectedBranchId) {
        params.append("branchId", user.selectedBranchId);
      }
      const data = await apiRequest(`/api/products/get-all?${params.toString()}`);

      const incoming = Array.isArray(data.products)
        ? data.products.filter((p) => p && p._id)
        : [];

      setProducts((prev) => {
        const prevValid = replace || effectiveSkip === 0
          ? []
          : prev.filter((p) => p && p._id && typeof p._id === "string");
        const combined = [...prevValid, ...incoming];
        return combined.filter((p, index, self) =>
          p && p._id && index === self.findIndex((t) => t._id === p._id)
        );
      });

      setSkip(effectiveSkip + incoming.length);
      setHasMore(data.hasMore);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Preserve scroll position when loading more products
  const handleLoadMore = async () => {
    // Find the scrollable parent container
    const container = containerRef.current;
    const scrollableParent = container?.closest('.products') || container?.parentElement;
    const prevScrollTop = scrollableParent?.scrollTop ?? 0;

    await getProducts();

    // Restore previous scroll position after DOM updates
    requestAnimationFrame(() => {
      if (scrollableParent) {
        scrollableParent.scrollTop = prevScrollTop;
      }
    });
  };

  // Initial fetch
  useEffect(() => {
    getProducts(0, true);
  }, []);

  // Refetch when search or selected branch (admin) changes
  useEffect(() => {
    setProducts([]);
    setSkip(0);
    setHasMore(true);
    getProducts(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, user?.selectedBranchId]);

  // Low stock notification: show when any loaded product has stock below threshold
  useEffect(() => {
    if (products.length === 0) {
      notification.destroy(LOW_STOCK_NOTIFICATION_KEY);
      return;
    }
    const lowStock = products.filter(
      (p) => typeof p.totalStock === "number" && p.totalStock < LOW_STOCK_THRESHOLD
    );
    if (lowStock.length === 0) {
      notification.destroy(LOW_STOCK_NOTIFICATION_KEY);
      return;
    }
    const maxList = 5;
    const listItems = lowStock
      .slice(0, maxList)
      .map((p) => `${p.title || "—"} (${p.totalStock})`);
    const listText = listItems.join(", ");
    const more = lowStock.length > maxList ? ` … +${lowStock.length - maxList} more` : "";
    notification.warning({
      key: LOW_STOCK_NOTIFICATION_KEY,
      message: tCommon("lowStockTitle"),
      description: tCommon("lowStockDescription", {
        count: lowStock.length,
        threshold: LOW_STOCK_THRESHOLD,
      }) + (listText ? `\n${listText}${more}` : ""),
      placement: "top",
      duration: 8,
    });
  }, [products, tCommon]);

  // React to category changes (add/update/delete) coming from the Categories modals.
  // This avoids needing a hard refresh for renamed categories to reflect in product list.
  useEffect(() => {
    const handleCategoryChanged = (event) => {
      const detail = event?.detail || {};

      // Fast-path: if we can update local cache on rename, do it (helps instant UI),
      // then refetch from server to guarantee consistency with backend.
      if (detail.type === "updated" && detail.oldTitle && detail.newTitle) {
        setProducts((prev) =>
          Array.isArray(prev)
            ? prev.map((p) =>
              p && p.category === detail.oldTitle
                ? { ...p, category: detail.newTitle }
                : p
            )
            : prev
        );
      }

      // Reset pagination and refetch
      setProducts([]);
      setSkip(0);
      setHasMore(true);
      getProducts(0, true);
    };

    window.addEventListener("categoryChanged", handleCategoryChanged);
    return () => {
      window.removeEventListener("categoryChanged", handleCategoryChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for product updates from cart and update local state
  useEffect(() => {
    const handleProductUpdate = (event) => {
      const { productId, title } = event.detail;
      if (!productId || !title) return;

      setProducts((prevProducts) => {
        // First ensure all products are valid
        const validProducts = prevProducts.filter((product) =>
          product && product._id && typeof product._id === 'string'
        );

        // Then update the matching product
        const updated = validProducts.map((product) =>
          product._id === productId ? { ...product, title: String(title).trim() } : product
        );

        // Final validation - ensure no invalid products slipped through
        return updated.filter((p) => p && p._id && typeof p._id === 'string');
      });
    };

    window.addEventListener("productUpdated", handleProductUpdate);
    return () => {
      window.removeEventListener("productUpdated", handleProductUpdate);
    };
  }, []);

  // Filter out any invalid products and apply category filter
  // Only require _id, title can be empty string (will show "Untitled" in ProductItem)
  const visibleProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    let filtered = products.filter((p) => p && p._id && typeof p._id === 'string');

    // Ignore trash data where product title is "0" or "3"
    filtered = filtered.filter((p) => {
      const title = String(p.title ?? "").trim();
      return title !== "0" && title !== "3";
    });

    if (selectedCategory !== "All") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    return filtered;
  }, [products, selectedCategory]);

  const canQuickAdd = useMemo(() => {
    return (
      user?.invoiceType !== "DIGITAL_INVOICING" &&
      !posType &&
      String(searched || "").trim().length > 0 &&
      !loading &&
      visibleProducts.length === 0
    );
  }, [user?.invoiceType, posType, searched, loading, visibleProducts.length]);

  const showExportButton = useMemo(() => {
    // Only allow non-cashier users (e.g. admin) to see the stock export button
    return user?.invoiceType === INVOICE_TYPE_POS && !posType && user?.role !== "cashier";
  }, [user?.invoiceType, posType, user?.role]);

  /** Escape a value for CSV (quote and escape internal quotes). */
  const escapeCsvCell = (value) => {
    const str = String(value ?? "").trim();
    const needsQuotes = /[",\n\r]/.test(str);
    return needsQuotes ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const handleExportClick = () => {
    if (exportLoading) return;
    Modal.confirm({
      title: t("exportStockTitle"),
      content: t("exportStockConfirm"),
      okText: tCommon("yes"),
      cancelText: tCommon("no"),
      onOk: () => exportStockAsCsv(),
    });
  };

  const exportStockAsCsv = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams({ page: "1", limit: "100000" });
      if (user?.role !== "cashier" && user?.selectedBranchId) {
        params.append("branchId", user.selectedBranchId);
      }
      const data = await apiRequest(`/api/products/get-all?${params.toString()}`);
      const allProducts = Array.isArray(data.products)
        ? data.products.filter((p) => p && p._id && typeof p._id === "string")
        : [];
      const filtered = allProducts.filter((p) => {
        const title = String(p.title ?? "").trim();
        return title !== "0" && title !== "3";
      });

      const businessName = user?.businessName || "Business";
      const reportTitle = "Stock Report";
      const dateStr = new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      const headerRows = [
        [escapeCsvCell(businessName)],
        [escapeCsvCell(reportTitle)],
        [escapeCsvCell(dateStr)],
        [],
        ["Product title", "Product stock"],
      ];
      const dataRows = filtered.map((p) => [
        escapeCsvCell(p.title ?? "Untitled"),
        String(p.totalStock ?? 0),
      ]);

      const csvContent = [...headerRows, ...dataRows]
        .map((row) => row.join(","))
        .join("\r\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(`Stock report exported (${filtered.length} products).`);
    } catch (err) {
      console.error(err);
      message.error(err?.message || "Failed to export stock report.");
    } finally {
      setExportLoading(false);
    }
  };

  const openEditModal = (product) => {
    if (!product?._id) return;
    setEditingProduct(product);
    setIsEditModalOpen(true);
    // Initialize form values
    editForm.setFieldsValue({
      title: product.title || "",
      price: product.price ?? 0,
      totalStock: product.totalStock ?? 0,
      hscode: product.hscode || "",
      category: product.category || DEFAULT_CATEGORY,
      uom: product.uom || UOM_NUMBERS_PIECES_UNITS,
      unit: product.unit ?? 0,
      salesTax:
        typeof product.salesTax === "number"
          ? `${product.salesTax}%`
          : "0%",
    });
  };

  const handleEditSubmit = async (values) => {
    if (!editingProduct?._id) return;

    const { _id, userId, invoiceType, ...restExisting } = editingProduct;
    const { salesTax, ...restValues } = values;
    const payload = {
      ...restExisting,
      ...restValues,
      salesTax: Number(String(salesTax || "").replace("%", "").trim()) || 0,
    };

    try {
      await apiRequest("/api/products/update-product", {
        method: "PUT",
        body: JSON.stringify({
          ...payload,
          productId: editingProduct._id,
        }),
      });

      setProducts((prev) =>
        prev.map((p) => (p._id === editingProduct._id ? { ...p, ...payload } : p))
      );

      message.success("Product updated successfully.");
      setIsEditModalOpen(false);
      setEditingProduct(null);
      editForm.resetFields();
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Failed to update product.");
    }
  };

  const handleDeleteProduct = (product) => {
    if (!product?._id) return;

    Modal.confirm({
      title: t("deleteProductConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: t("deleteProductContent", { name: product.title || t("thisProduct") }),
      okText: t("delete"),
      okType: "danger",
      cancelText: tCommon("cancel"),
      async onOk() {
        try {
          await apiRequest("/api/products/delete-product", {
            method: "DELETE",
            body: JSON.stringify({ productId: product._id }),
          });

          setProducts((prev) => prev.filter((p) => p && p._id !== product._id));
          message.success("Product deleted successfully.");
        } catch (error) {
          console.error(error);
          message.error(error?.message || "Failed to delete product.");
        }
      },
    });
  };

  const openQuickAdd = () => {
    const name = String(searched || "").trim();
    const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
    quickAddForm.setFieldsValue({
      title: formattedName,
      price: undefined,
      totalStock: 100000,
      uom: UOM_NUMBERS_PIECES_UNITS,
      hscode: undefined,
      category: DEFAULT_CATEGORY,
      salesTax: undefined,
      unit: undefined
    });
    setIsQuickAddOpen(true);
  };

  const handleQuickAdd = async (values) => {
    const payload = {
      title: String(values.title || "").trim(),
      price: Number(values.price) || 0,
      totalStock: Number(values.totalStock) || 100000,
      uom: values.uom,
      hscode: String(values.hscode || "").trim(),
      category: values.category || DEFAULT_CATEGORY,
      salesTax: values.salesTax
        ? Number(String(values.salesTax).replace("%", "").trim()) || 0
        : 0,
      unit: Number(values.packQuantity) || 0
    };

    const effectiveBranchId = user?.branchId || user?.selectedBranchId;
    if (effectiveBranchId) payload.branchId = effectiveBranchId;
    try {
      const res = await apiRequest("/api/products/add-product", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const created = res?.product;
      if (!created?._id) {
        message.error("Product created but response was unexpected. Please refresh.");
        return;
      }

      const productToAdd = {
        ...payload,
        ...created,
        price: Number(payload.price) || 0,
        totalStock: Number(payload.totalStock) || 0,
        title: payload.title || created.title || "Untitled",
      };

      setProducts((prev) => {
        const prevValid = Array.isArray(prev) ? prev.filter((p) => p && p._id) : [];
        const withoutDup = prevValid.filter((p) => p._id !== productToAdd._id);
        return [productToAdd, ...withoutDup];
      });

      setIsQuickAddOpen(false);
      quickAddForm.resetFields();
      message.success("Item added and placed into basket");

      // Auto add to basket (cartSlice will validate HS code tax mapping)
      dispatch(addProductToCart(productToAdd));
    } catch (err) {
      console.error(err);
      message.error(err?.message || "Failed to quick add item");
    }
  };

  // Force grid layout recalculation when products change
  // This fixes the CSS Grid layout issue where items don't reposition correctly after state updates
  // Also ensures the scroll container maintains its scroll capability
  useLayoutEffect(() => {
    if (containerRef.current && !loading) {
      const container = containerRef.current;
      const scrollableParent = container?.closest('.products') || container?.parentElement;

      // Force grid recalculation using double RAF to ensure DOM is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container && scrollableParent) {
            // Reading offsetWidth forces a layout recalculation
            // This is the same mechanism that zooming triggers
            void container.offsetWidth;
            void container.offsetHeight;

            // Ensure scroll container maintains proper scroll capability
            // Force recalculation of scrollable area
            const scrollHeight = scrollableParent.scrollHeight;
            const clientHeight = scrollableParent.clientHeight;

            // If content is taller than container, ensure overflow is visible
            if (scrollHeight > clientHeight) {
              // Force browser to recalculate scrollbar
              scrollableParent.style.overflowY = 'auto';
            }
          }
        });
      });
    }
  }, [visibleProducts.length, loading]);

  return (
    <div className="products-wrapper flex flex-col gap-4 min-h-full">
      {/* Quick Add CTA (only when search has no matches and POS type is not restaurant/hotel) */}
      {canQuickAdd && (
        <div className="rounded-md border bg-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium truncate text-gray-800">
              No product found for: <span className="text-blue-600">{String(searched || "").trim()}</span>
            </div>
            <div className="text-xs text-gray-600">
              Create it instantly and it will be added to the basket.
            </div>
          </div>
          <Button type="primary" onClick={openQuickAdd}>
            Quick Add Item and Add to basket
          </Button>
        </div>
      )}

      {/* Action Buttons - Top Section: equal-width Add & Edit; Export icon when POS and !posType */}
      <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: showExportButton ? "1fr 1fr auto" : "1fr 1fr" }}>
        {/* ➕ Add Product */}
        <div
          className="group h-12 rounded-md cursor-pointer transition-all duration-300 select-none flex flex-row items-center justify-center gap-2 px-4 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
          onClick={() => setIsAddModalOpen(true)}
        >
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
          <div className="relative z-10 flex items-center justify-center">
            <PlusOutlined className="text-lg text-white drop-shadow-sm" />
          </div>
          <span className="relative z-10 text-white font-medium text-sm tracking-wide drop-shadow-sm">
            {t("addProduct")}
          </span>
        </div>

        {/* ✏️ Edit Products */}
        <div
          className="group h-12 rounded-md cursor-pointer transition-all duration-300 select-none flex flex-row items-center justify-center gap-2 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
          onClick={() => navigate("/products")}
        >
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
          <div className="relative z-10 flex items-center justify-center">
            <EditOutlined className="text-lg text-white drop-shadow-sm" />
          </div>
          <span className="relative z-10 text-white font-medium text-sm tracking-wide drop-shadow-sm">
            {t("editProducts")}
          </span>
        </div>

        {/* 📥 Export (icon only) - only for invoiceType POS and posType false */}
        {showExportButton && (
         <div
         className={`group h-12 w-12 shrink-0 rounded-lg transition-all duration-200 select-none flex items-center justify-center
           bg-slate-100 hover:bg-slate-200
           shadow-sm hover:shadow-md
           border border-slate-200
           ${exportLoading ? "opacity-90 cursor-wait" : "cursor-pointer active:scale-[0.97] hover:scale-[1.01]"}`}
         onClick={handleExportClick}
         title="Export stock (CSV)"
       >
         {exportLoading ? (
           <LoadingOutlined className="text-lg text-slate-600 group-hover:text-slate-800 transition-colors duration-200" spin />
         ) : (
           <DownloadOutlined className="text-lg text-slate-600 group-hover:text-slate-800 transition-colors duration-200" />
         )}
       </div>
        )}
      </div>

      {/* Products container - scroll is handled by parent */}
      <div
        ref={containerRef}
        className="grid grid-cols-card gap-4"
      >
        {/* 🔹 Skeleton Loading */}
        {loading && Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="min-h-[180px] border rounded-md p-4">
            <Skeleton
              active
              avatar={{ shape: "square", size: 64 }}
              title={{ width: "70%" }}
              paragraph={{ rows: 2 }}
            />
          </div>
        ))}

        {/* Products */}
        {!loading &&
          visibleProducts.map((item) => (
            <ProductItem
              item={item}
              key={item._id}
              onEdit={openEditModal}
              onDelete={handleDeleteProduct}
            />
          ))}
      </div>

      {/* ➕ Load More Button (only when there are visible products) */}
      {hasMore && !loading && visibleProducts.length > 0 && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Load More
          </button>
        </div>
      )}

      {/* ➕ Add Modal */}
      <Add
        isAddModalOpen={isAddModalOpen}
        setIsAddModalOpen={setIsAddModalOpen}
        products={products}
        setProducts={setProducts}
        categories={categories}
      />

      {/* Quick Add Modal */}
      <Modal
        title={t("quickAddItem")}
        open={isQuickAddOpen && user?.invoiceType !== "DIGITAL_INVOICING"}
        onCancel={() => setIsQuickAddOpen(false)}
        footer={null}
        destroyOnHidden
        width={700}
      >
        <Form
          form={quickAddForm}
          layout="vertical"
          onFinish={handleQuickAdd}
          initialValues={{ category: DEFAULT_CATEGORY }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", marginBottom: "16px" }}>
            {/* Product Name */}
            <Form.Item
              label={t("productName")}
              name="title"
              rules={[{ required: true, message: "Product name is required" }]}
            >
              <Input placeholder={t("enterProductName")} autoFocus />
            </Form.Item>

            {/* Pack Price */}
            <Form.Item
              label={t("packPrice")}
              name="price"
              rules={[{ required: true, message: "Pack price is required" }]}
            >
              <InputNumber
                min={0}
                placeholder={t("enterPackPrice")}
                style={{ width: "100%" }}
                onChange={() => {
                  const price = quickAddForm.getFieldValue("price") || 0;
                  const qty = quickAddForm.getFieldValue("packQuantity") || 1;
                  quickAddForm.setFieldValue("singleUnitPrice", qty ? price / qty : 0);
                }}
              />
            </Form.Item>

            {/* Pack Quantity */}
            {user?.invoiceType !== "DIGITAL_INVOICING" && (
              <Form.Item
                label={t("packQuantity")}
                name="packQuantity"
                rules={[{ required: true, message: "Pack quantity is required" }]}
              >
                <InputNumber
                  min={1}
                  placeholder={t("enterQuantity")}
                  style={{ width: "100%" }}
                  onChange={() => {
                    const price = quickAddForm.getFieldValue("price") || 0;
                    const qty = quickAddForm.getFieldValue("packQuantity") || 1;
                    quickAddForm.setFieldValue("singleUnitPrice", qty ? price / qty : 0);
                  }}
                />
              </Form.Item>
            )}

            {/* Single Unit Price */}
            {user?.invoiceType !== "DIGITAL_INVOICING" && (
              <Form.Item
                label={t("unitPriceAuto")}
                name="singleUnitPrice"
              >
                <InputNumber
                  min={0}
                  placeholder={t("autoCalculated")}
                  style={{ width: "100%" }}
                  disabled
                />
              </Form.Item>
            )}

            {/* HS Code */}
            <Form.Item
              label={
                <span>
                  HS Code{" "}
                  {!posType && (
                    <Popover
                      placement="right"
                      title={t("commonHSCodes")}
                      trigger="click"
                      overlayStyle={{ maxWidth: 300 }}
                      content={
                        <div style={{ maxWidth: 280, maxHeight: 400, overflowY: "auto", padding: "4px 0" }}>
                          <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                            Click a code to fill the HS Code field.
                          </div>
                          {HS_CODE_HINTS && HS_CODE_HINTS.length > 0 ? (
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, listStyle: "disc" }}>
                              {HS_CODE_HINTS.map((item) => (
                                <li 
                                  key={item.code} 
                                  style={{ 
                                    marginBottom: 6, 
                                    lineHeight: 1.5,
                                    cursor: "pointer",
                                    color: "#1890ff",
                                  }}
                                  onClick={() => {
                                    quickAddForm.setFieldsValue({ hscode: item.code });
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = "underline";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = "none";
                                  }}
                                >
                                  <strong style={{ color: "#1890ff" }}>{item.label}</strong>
                                  <span style={{ color: "#1890ff" }}>: {item.code}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div style={{ color: "#999", fontSize: 12 }}>No HS codes available</div>
                          )}
                        </div>
                      }
                    >
                      <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 4, cursor: "pointer" }} />
                    </Popover>
                  )}
                </span>
              }
              name="hscode"
              rules={[{ required: true, message: "HS Code is required" }]}
            >
              <Input placeholder={t("enterHSCode")} />
            </Form.Item>

            {/* Sales Tax */}
            <Form.Item
              label={t("salesTax")}
              name="salesTax"
              rules={[{ required: true, message: "Sales Tax is required" }]}
            >
              <Select placeholder={t("selectSalesTax")} style={{ width: "100%" }}>
                {SALES_TAX_RATES.map((rate) => (
                  <Select.Option key={rate} value={rate}>
                    {rate}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Unit of Measurement */}
            <Form.Item
              label={t("unitOfMeasurement")}
              name="uom"
              rules={[{ required: true, message: "UoM is required" }]}
            >
              <Select placeholder={t("selectUom")} style={{ width: "100%" }}>
                {UOM_OPTIONS.map((uom) => (
                  <Select.Option key={uom} value={uom}>
                    {uom === UOM_NUMBERS_PIECES_UNITS
                      ? uom
                      : uom === "MT"
                      ? "Metric Ton (MT)"
                      : uom === "KG"
                      ? "Kilogram (KG)"
                      : uom === "Grams"
                      ? "Grams (G)"
                      : uom === "Packs"
                      ? "Packs"
                      : "Milligram (MG)"}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Hidden category for backend */}
          <Form.Item name="category" hidden>
            <Input />
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginTop: "24px", marginBottom: 0 }}>
            <Button 
              onClick={() => setIsQuickAddOpen(false)} 
              style={{ marginRight: "8px" }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Add Item
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Inline Edit Product Modal */}
      <Modal
        title={t("editProduct")}
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingProduct(null);
          editForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          layout="vertical"
          form={editForm}
          onFinish={handleEditSubmit}
          initialValues={{
            category: DEFAULT_CATEGORY,
            uom: UOM_NUMBERS_PIECES_UNITS,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <Form.Item
              label={t("productName")}
              name="title"
              rules={[{ required: true, message: "Product name is required" }]}
              style={{ flex: "1 1 220px" }}
            >
              <Input placeholder={t("namePlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("packPrice")}
              name="price"
              rules={[{ required: true, message: "Price is required" }]}
              style={{ flex: "1 1 160px" }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={t("pricePlaceholder")}
              />
            </Form.Item>

            {!posType && editingProduct?.invoiceType !== "DIGITAL_INVOICING" && (
              <Form.Item
                label={t("packQuantity")}
                name="unit"
                rules={[{ required: true, message: "Quantity is required" }]}
                style={{ flex: "1 1 140px" }}
              >
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  placeholder={t("qtyPlaceholder")}
                />
              </Form.Item>
            )}

            <Form.Item
              label={t("totalStock")}
              name="totalStock"
              style={{ flex: "1 1 160px" }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={t("totalStockPlaceholder")}
              />
            </Form.Item>

            <Form.Item
              label={t("hsCode")}
              name="hscode"
              rules={[{ required: true, message: "HS Code is required" }]}
              style={{ flex: "1 1 180px" }}
            >
              <Input placeholder={t("hsCode")} />
            </Form.Item>

            <Form.Item
              label={t("salesTax")}
              name="salesTax"
              rules={[{ required: true, message: "Sales Tax is required" }]}
              style={{ flex: "1 1 160px" }}
            >
              <Select placeholder={t("selectTax")}>
                {SALES_TAX_RATES.map((rate) => (
                  <Select.Option key={rate} value={rate}>
                    {rate}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t("selectUomShort")}
              name="uom"
              rules={[{ required: true, message: "UoM is required" }]}
              style={{ flex: "1 1 200px" }}
            >
              <Select placeholder={t("selectUomShort")}>
                {UOM_OPTIONS.map((uom) => (
                  <Select.Option key={uom} value={uom}>
                    {uom === UOM_NUMBERS_PIECES_UNITS
                      ? uom
                      : uom === "MT"
                      ? "Metric Ton (MT)"
                      : uom === "KG"
                      ? "Kilogram (KG)"
                      : uom === "Grams"
                      ? "Grams (G)"
                      : uom === "Packs"
                      ? "Packs"
                      : "Milligram (MG)"}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t("category")}
              name="category"
              style={{ flex: "1 1 200px" }}
            >
              <Select placeholder={t("selectCategory")}>
                <Select.Option value={DEFAULT_CATEGORY}>{t("all")}</Select.Option>
                {Array.isArray(categories) &&
                  categories.map((cat) => (
                    <Select.Option key={cat._id || cat.title} value={cat.title}>
                      {cat.title}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item style={{ textAlign: "right", marginTop: 8 }}>
            <Button onClick={() => {
              setIsEditModalOpen(false);
              setEditingProduct(null);
              editForm.resetFields();
            }} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default Products;
